import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from "recharts";
import { Users, MousePointerClick, TrendingUp, Monitor, Smartphone, Tablet, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { key: "today", label: "Aujourd'hui" },
  { key: "7d", label: "7 jours" },
  { key: "30d", label: "30 jours" },
  { key: "3m", label: "3 mois" },
  { key: "12m", label: "12 mois" },
  { key: "custom", label: "Personnalisé" },
];

const PAGE_LABELS = {
  "/": "Accueil",
  "/vehicules": "Véhicules",
  "/reservation": "Réservation",
};

function formatDate(dateStr, isMonth) {
  if (!dateStr) return "";
  if (isMonth) {
    const [y, m] = dateStr.split("-");
    const months = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
    return `${months[parseInt(m) - 1]} ${y}`;
  }
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}`;
}

const CustomTooltip = ({ active, payload, label, isMonth }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 shadow-lg p-3 rounded text-sm">
      <p className="font-semibold text-gray-700 mb-1">{formatDate(label, isMonth)}</p>
      {payload.map(p => (
        <p key={p.name} style={{ color: p.color }}>
          {p.name === "visits" ? "Visites" : "Sessions uniques"}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

export default function AdminStats() {
  const { authHeaders } = useAuth();
  const [period, setPeriod] = useState("30d");
  const [customStart, setCustomStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
  });
  const [customEnd, setCustomEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === "custom") { params.start = customStart; params.end = customEnd; }
      const res = await axios.get(`${API}/analytics/traffic`, { params, headers: authHeaders });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [period, customStart, customEnd, authHeaders]);

  useEffect(() => { if (period !== "custom") fetchData(); }, [fetchData, period]);

  const isMonth = data?.rows?.length > 0 && data.rows[0]?.date?.length === 7;
  const chartData = (data?.rows || []).map(r => ({
    ...r,
    label: formatDate(r.date, isMonth),
  }));

  const deviceData = (data?.devices || []).map(d => ({
    device: d.device === "mobile" ? "Mobile" : d.device === "tablet" ? "Tablette" : "Desktop",
    count: d.count,
    icon: d.device === "mobile" ? "mobile" : d.device === "tablet" ? "tablet" : "desktop",
  }));
  const totalDevices = deviceData.reduce((s, d) => s + d.count, 0);

  const DEVICE_COLORS = { Desktop: "#1B2E4B", Mobile: "#D4A843", Tablette: "#059669" };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="stats-title">
            Statistiques de fréquentation
          </h1>
          <p className="text-gray-500 text-sm mt-1">Visites du site public rmlocation.re</p>
        </div>
      </div>

      {/* Period selector */}
      <div className="bg-white p-4 shadow-sm rounded mb-6 flex flex-wrap items-center gap-3">
        <div className="flex gap-2 flex-wrap">
          {PERIODS.map(p => (
            <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`period-${p.key}`}
              className={`px-4 py-2 text-sm font-semibold rounded border-2 transition-all ${
                period === p.key ? "bg-[#1B2E4B] border-[#1B2E4B] text-white" : "border-gray-200 text-gray-600 hover:border-gray-400"
              }`}>
              {p.label}
            </button>
          ))}
        </div>

        {period === "custom" && (
          <div className="flex items-center gap-2 ml-2">
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
              className="border border-gray-200 px-3 py-2 text-sm rounded focus:outline-none focus:border-blue-400" />
            <span className="text-gray-400">→</span>
            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              className="border border-gray-200 px-3 py-2 text-sm rounded focus:outline-none focus:border-blue-400" />
            <button onClick={fetchData}
              className="px-4 py-2 bg-[#1B2E4B] text-white text-sm font-semibold rounded hover:bg-[#162440]">
              Appliquer
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !data ? (
        <p className="text-gray-400 text-center py-16">Erreur de chargement.</p>
      ) : (
        <div className="space-y-6">
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Visites totales", value: data.summary.total_visits, icon: MousePointerClick, color: "text-[#1B2E4B]", bg: "bg-blue-50" },
              { label: "Sessions uniques", value: data.summary.unique_sessions, icon: Users, color: "text-purple-600", bg: "bg-purple-50" },
              { label: "Moy. visites / jour", value: data.summary.avg_daily, icon: TrendingUp, color: "text-amber-600", bg: "bg-amber-50" },
              { label: "Période", value: `${data.summary.period_days} jours`, icon: Download, color: "text-green-600", bg: "bg-green-50" },
            ].map(({ label, value, icon: Icon, color, bg }) => (
              <div key={label} className="bg-white p-5 shadow-sm rounded">
                <div className={`w-9 h-9 ${bg} rounded flex items-center justify-center mb-3`}>
                  <Icon size={18} className={color} />
                </div>
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
              </div>
            ))}
          </div>

          {/* Main chart */}
          <div className="bg-white p-6 shadow-sm rounded">
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-5">
              Visites au fil du temps
            </h2>
            {chartData.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
                Aucune donnée pour cette période. Les visites s'accumuleront au fil du temps.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip isMonth={isMonth} />} />
                  <Line type="monotone" dataKey="visits" name="visits" stroke="#1B2E4B" strokeWidth={2.5}
                    dot={chartData.length < 40 ? { fill: "#1B2E4B", r: 3 } : false}
                    activeDot={{ r: 5, fill: "#1B2E4B" }} />
                  <Line type="monotone" dataKey="unique_sessions" name="sessions" stroke="#D4A843"
                    strokeWidth={2} strokeDasharray="4 2"
                    dot={false} activeDot={{ r: 4, fill: "#D4A843" }} />
                </LineChart>
              </ResponsiveContainer>
            )}
            <div className="flex gap-4 mt-2 justify-end">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <div className="w-6 h-0.5 bg-[#1B2E4B] rounded" /> Visites totales
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <div className="w-6 border-t-2 border-dashed border-[#D4A843]" /> Sessions uniques
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Top pages */}
            <div className="bg-white p-6 shadow-sm rounded">
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-5">Pages les plus visitées</h2>
              {data.top_pages.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-8">Aucune donnée disponible</p>
              ) : (
                <div className="space-y-3">
                  {data.top_pages.map((p, i) => {
                    const maxCount = data.top_pages[0]?.count || 1;
                    const pct = Math.round((p.count / maxCount) * 100);
                    const label = PAGE_LABELS[p.page] || p.page;
                    return (
                      <div key={p.page}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-700 truncate max-w-[180px]" title={p.page}>
                            <span className="text-gray-400 mr-2 text-xs">{i + 1}.</span>{label}
                          </span>
                          <span className="font-bold text-gray-900 flex-shrink-0 ml-2">{p.count}</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-1.5 bg-[#1B2E4B] rounded-full transition-all"
                            style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Devices */}
            <div className="bg-white p-6 shadow-sm rounded">
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-5">Appareils</h2>
              {deviceData.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-8">Aucune donnée disponible</p>
              ) : (
                <div className="space-y-4">
                  {deviceData.map(d => {
                    const pct = totalDevices > 0 ? Math.round((d.count / totalDevices) * 100) : 0;
                    const Icon = d.icon === "mobile" ? Smartphone : d.icon === "tablet" ? Tablet : Monitor;
                    const color = DEVICE_COLORS[d.device] || "#6b7280";
                    return (
                      <div key={d.device}>
                        <div className="flex items-center justify-between text-sm mb-1.5">
                          <div className="flex items-center gap-2">
                            <Icon size={14} style={{ color }} />
                            <span className="font-medium text-gray-700">{d.device}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-gray-400 text-xs">{d.count} visites</span>
                            <span className="font-bold text-gray-900">{pct}%</span>
                          </div>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-2 rounded-full transition-all"
                            style={{ width: `${pct}%`, backgroundColor: color }} />
                        </div>
                      </div>
                    );
                  })}
                  <div className="pt-3 border-t border-gray-100">
                    <ResponsiveContainer width="100%" height={140}>
                      <BarChart data={deviceData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="device" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="count" name="Visites" radius={[4, 4, 0, 0]}>
                          {deviceData.map(d => (
                            <Cell key={d.device} fill={DEVICE_COLORS[d.device] || "#6b7280"} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
